import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { Box, Copy, H6 } from '@honda-canada/design-system-react';
import { useTranslation } from 'react-i18next';
import { compiler } from 'markdown-to-jsx';
import gtmExteriorColorsClick from '../../core/gtm/gtmExteriorColorsClick';
import ColorSwatchCarousel from './shared/ColorSwatchCarousel';
import OtherTrimColours from './shared/OtherTrimColours';
import { selectCurrentVehicle, setColors } from '../../core/reducers/currentVehicle';
import { stripMarkdownHeading } from '../../utils/markdown';
import { useAppDispatch, useAppSelector } from '../../core/utils';
import { useFetchCurrentVehiclePayment } from '../../core/apiHooks/useFetchPayment';
import useColorSwatchWithUpCharge, { ColorType } from './hooks/useColorSwatchWithUpCharge';
import Loader from '../../utils/components/Loader';
import Error from '../PriceComponent/Error';
import { setSelectedAssetType, assetType } from '../../core/reducers/ui';
import { selectBodyTypesForGtm, selectCurrentVehicleFromVehicles } from '../../core/reducers/vehicles';
import gtmSliderArrowClick from '../../core/gtm/gtmSliderArrowClick';
import { EXTERIOR } from './utils/constants';
import useBapRegisterUserInteraction from '../../utils/hooks/bap/useBapRegisterUserInteraction';
const Title = ({ label, id }) => (_jsx(H6, { as: "h3", mb: "xxs", "data-testid": "cy-config-exteriorcolors", id: id, children: label }));
const ExteriorColorsPanel = ({ id, colors, label, categoryId, isFetching, hasColorError, componentName, }) => {
    var _a;
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const { registerUserInteraction } = useBapRegisterUserInteraction('config');
    const gtmBodyStyle = useAppSelector(selectBodyTypesForGtm);
    const currentVehicle = useAppSelector(selectCurrentVehicleFromVehicles);
    const { transmissionKey, exteriorColorKey, inventoryStatus } = useAppSelector(selectCurrentVehicle);
    const { data: paymentData, error: isPaymentFailed } = useFetchCurrentVehiclePayment();
    const showError = isPaymentFailed || hasColorError;
    const colorsByCurrentTransmission = colors.find(c => c.transmissionKey === transmissionKey);
    const selectedColor = (_a = colorsByCurrentTransmission === null || colorsByCurrentTransmission === void 0 ? void 0 : colorsByCurrentTransmission.exteriorColors) === null || _a === void 0 ? void 0 : _a.find(c => (c === null || c === void 0 ? void 0 : c.key) === exteriorColorKey);
    const swatches = useColorSwatchWithUpCharge({
        colors: colorsByCurrentTransmission === null || colorsByCurrentTransmission === void 0 ? void 0 : colorsByCurrentTransmission.exteriorColors,
        colorsUpCharge: paymentData === null || paymentData === void 0 ? void 0 : paymentData.exteriorColors,
        colorType: ColorType.EXT,
    });
    const gtmPayload = {
        model: currentVehicle === null || currentVehicle === void 0 ? void 0 : currentVehicle.modelGtmName,
        trim: currentVehicle === null || currentVehicle === void 0 ? void 0 : currentVehicle.trimGtmName,
        gtmBodyStyle,
        availabilityFlag: inventoryStatus,
    };
    const handleClick = (key) => {
        var _a;
        const currentColor = (_a = colorsByCurrentTransmission === null || colorsByCurrentTransmission === void 0 ? void 0 : colorsByCurrentTransmission.exteriorColors) === null || _a === void 0 ? void 0 : _a.find(c => (c === null || c === void 0 ? void 0 : c.key) === key);
        const interiorColorKey = currentColor === null || currentColor === void 0 ? void 0 : currentColor.defaultInteriorColor;
        const exteriorColorName = currentColor === null || currentColor === void 0 ? void 0 : currentColor.name;
        gtmExteriorColorsClick({
            ...gtmPayload,
            colour: exteriorColorName,
        });
        if (key && interiorColorKey) {
            registerUserInteraction();
            // TODO: Syncing URL with Redux state refactor
            const searchParams = new URLSearchParams(window.location.search);
            searchParams.set('exteriorColorKey', key);
            searchParams.set('interiorColorKey', interiorColorKey);
            const currentHash = window.location.hash;
            const updatedParams = `${window.location.pathname}?${decodeURIComponent(searchParams.toString())}${currentHash}`;
            //  sync URL with state
            window.history.replaceState({}, '', updatedParams);
            // reset toggle to show exterior image
            dispatch(setSelectedAssetType(assetType.exterior));
            dispatch(setColors({
                exteriorColor: key,
                interiorColor: interiorColorKey,
            }));
        }
    };
    if (isFetching) {
        return (_jsxs(Box, { py: ['m', 'm', 'l'], "data-testid": "cy-exteriorcolors-panel", children: [_jsx(Title, { id: id, label: label }), _jsx(Loader, { testId: "cy-loading-state" })] }));
    }
    if (showError) {
        return (_jsxs(Box, { py: ['m', 'm', 'l'], "data-testid": "cy-exteriorcolors-panel", children: [_jsx(Title, { id: id, label: label }), _jsx(Error, { message: t('Pages.Bap.apiLoadingError'), verticalAlignment: "center", errorIconName: "exclamationAlert" })] }));
    }
    return (_jsxs(Box, { py: ['m', 'm', 'l'], "data-testid": "cy-exteriorcolors-panel", children: [_jsx(Title, { id: id, label: label }), _jsx(Copy, { "data-testid": "cy-swatch-color-label", size: "extraSmall", mb: "m", fontWeight: "bold", children: compiler(stripMarkdownHeading(selectedColor === null || selectedColor === void 0 ? void 0 : selectedColor.colorName)) }), _jsx(ColorSwatchCarousel, { swatchTestId: categoryId, swatches: swatches, handleClick: handleClick, onSliderArrowClick: () => gtmSliderArrowClick({
                    ...gtmPayload,
                    sectionName: EXTERIOR,
                }), selectedColor: selectedColor === null || selectedColor === void 0 ? void 0 : selectedColor.key }), _jsx(OtherTrimColours, { componentName: componentName })] }));
};
export default ExteriorColorsPanel;
